import React from 'react';
import { useIsMobile } from '@uplift/hooks';
import { ModalFullPage } from '../../../ModalFullPage';
import { SessionDetailsModalContent } from './components/SessionDetailsGeneral';
import { SessionDetailsNotes } from './components/SessionDetailsNotes';
import { SessionDetailsAppointmentArg, useSessionDetailsModalData } from './SessionDetailsModal.hook';
import { useICSSubscriptionEventsQuery } from '@uplift/queries';
import { useCalendarStore, useCurrentuser } from '@uplift/contexts';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { SessionDetailsHeader } from './components/SessionDetailsHeader';
import { CalendarDateService } from '@uplift/services';


const useStyles = makeStyles({ name: 'ICSConflictsResolutionModal' })(theme => ({
  modal: {
    position: 'relative',
    overflow: 'visible',
  },
  floatingButton: {
    position: 'absolute',
    top: '50%',
    backgroundColor: theme.palette.common.white,

    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    }
  },
  floatingLeft: {
    right: 'calc(100% + 40px)',
  },
  floatingRight: {
    left: 'calc(100% + 40px)',
  },
}));


type ICSConflictsResolutionModalsProps = {
  open: boolean;
  onClose: () => void;
  keepMounted?: boolean;
};


const ICSConflictsResolutionModals: React.FC<ICSConflictsResolutionModalsProps & {
  appointment: SessionDetailsAppointmentArg;
  conflictsCount: number;
  goNext?: () => void;
  goPrev?: () => void;
}> = (props) => {
  const calendarStore = useCalendarStore();
  const [ page, setPage ] = React.useState<'general' | 'notes'>('general');
  const { data, isLoading } = useSessionDetailsModalData(props.open ? props.appointment : undefined);
  const isMobile = useIsMobile();
  const { classes, cx } = useStyles();

  const handleClose = () => {
    props.onClose();
    setPage('general');
  };

  return (
    <>
      <ModalFullPage
        data-appointment-id={props.appointment.id}
        open={props.open}
        onClose={handleClose}
        mobileOnly="md"
        maxWidth="sm"
        transition={isMobile}
        keepMounted={props.keepMounted}
        classes={{ paper: classes.modal }}
      >
        <SessionDetailsHeader
          onClose={props.onClose}
          appointment={data.appointment}
          title={props.conflictsCount === 1
            ? `${props.conflictsCount} Conflict`
            : `${props.conflictsCount} Conflicts`
          }
        />
        <SessionDetailsModalContent
          loading={isLoading}
          onReviewNotes={() => setPage('notes')}
          appointment={data?.appointment}
          previousAppointment={data?.previousAppointment}
          patientDetails={data?.patientDetails}
          onCancelSession={appointment => {
            calendarStore.setSelectedEvent({
              id: appointment.id,
              start: CalendarDateService.date(appointment.fromDatetime).toDate(),
              end: CalendarDateService.date(appointment.toDatetime).toDate(),
              extendedProps: {
                id: appointment.id,
                parentId: appointment.appointmentSeriesId,
                fromDatetime: appointment.fromDatetime,
                patientId: appointment.patientId,
                isMaterialized: appointment.isMaterialized,
              }
            });

            calendarStore.openModal('cancel');
          }}
        />
        {props.goPrev && (
          <IconButton className={cx(classes.floatingButton, classes.floatingLeft)} onClick={props.goPrev}>
            <ChevronLeft />
          </IconButton>
        )}
        {props.goNext && (
          <IconButton className={cx(classes.floatingButton, classes.floatingRight)} onClick={props.goNext}>
            <ChevronRight />
          </IconButton>
        )}
      </ModalFullPage>
      <ModalFullPage
        open={page === 'notes'}
        onClose={handleClose}
        mobileOnly="md"
        maxWidth="sm"
        transition={isMobile}
        keepMounted={props.keepMounted}
        slotProps={{
          backdrop: { invisible: true }
        }}
      >
        <SessionDetailsNotes
          open={page === 'notes'}
          onBack={() => setPage('general')}
          appointmentId={data?.previousAppointment?.id}
          appointmentDateTime={data?.previousAppointment?.fromDatetime}
          patientId={data?.appointment?.patientId}
        />
      </ModalFullPage>
    </>
  );
};


export const ICSConflictsResolutionModal: React.VFC<ICSConflictsResolutionModalsProps> = (props) => {
  const me = useCurrentuser();
  const { data } = useICSSubscriptionEventsQuery(me.id);
  const conflicts = data?.conflicts ?? [];
  const [ currentConflict, setCurrentConflict ] = React.useState(0);

  React.useEffect(() => {
    if (conflicts.length === 0) {
      props.onClose();
    }
  }, [ conflicts.length ]);

  if (!conflicts.length) {
    return null;
  }

  return (
    <React.Suspense fallback={null}>
      <ICSConflictsResolutionModals
        {...props}
        conflictsCount={conflicts.length}
        appointment={{
          id: conflicts[currentConflict].appointment.id,
          parentId: conflicts[currentConflict].appointment.appointmentSeriesId ?? '',
          fromDatetime: conflicts[currentConflict].appointment.fromDatetime,
          isMaterialized: conflicts[currentConflict].appointment.isMaterialized,
        }}
        goPrev={currentConflict === 0 ? undefined : (() => setCurrentConflict(currentConflict - 1))}
        goNext={currentConflict >= conflicts.length - 1 ? undefined : (() => setCurrentConflict(currentConflict + 1))}
      />
    </React.Suspense>
  );
};
